import React, { useState, useRef } from "react";
import { toPng } from "html-to-image";
import { patch, destroy } from "@rails/request.js";
import { PiWaveform, PiTextAlignLeft, PiVideoCamera } from "react-icons/pi";
import { SiX, SiLinkedin, SiDiscord } from "react-icons/si";
import { Toasts, useToasts } from "./../shared/Toast";
import { Modal, useModal } from "./../shared/Modal";

const kindIcon = (proof) => {
  if (proof.source_platform === "Discord")   return <SiDiscord />;
  if (proof.source_platform === "LinkedIn")  return <SiLinkedin />;
  if (proof.source_platform === "Twitter/X") return <SiX />;
  if (proof.kind === "video") return <PiVideoCamera />;
  if (proof.kind === "audio") return <PiWaveform />;
  return <PiTextAlignLeft />;
};

export const ProofManage = ({ initProof, widgets = [], projectLabels = [], labelsUrl, projectId, prevUrl, nextUrl }) => {
  const [proof, setProof] = useState(initProof.proof);
  const [deleted, setDeleted] = useState(false);
  const { modalOpen: confirmOpen, setModalOpen: setConfirmOpen } = useModal(false);
  const [deleting, setDeleting] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { toasts, setToasts, addToast } = useToasts();
  const cardRef = useRef(null);

  const handleApproveChange = async () => {
    const response = await patch(`/api/v1/proofs/${proof.id}`, {
      body: { proof: { approved: !proof.approved } },
      contentType: "application/json",
      responseKind: "json",
    });
    if (response.ok) {
      const json = await response.json;
      setProof(json.proof);
      addToast("success", "Saved!");
    } else {
      addToast("error", "Something went wrong. Please try again.");
    }
  };

  // Server find-or-creates by name scoped to the project, so we send
  // names rather than ids — lets the user type brand-new labels without
  // a separate "create" round-trip.
  const persistLabelNames = async (nextNames) => {
    const currentNames = (proof.labels || []).map((l) => l.name);
    if (nextNames.join("|") === currentNames.join("|")) return;
    const response = await patch(`/api/v1/proofs/${proof.id}`, {
      body: { proof: { label_names: nextNames } },
      contentType: "application/json",
      responseKind: "json",
    });
    if (response.ok) {
      const json = await response.json;
      setProof(json.proof);
      addToast("success", nextNames.length === 0 ? "Labels cleared." : `Labeled: ${json.proof.labels.map((l) => l.name).join(", ")}`);
    } else {
      addToast("error", "Could not save labels.");
    }
  };

  const handleLabelsAdd = (raw) => {
    const incoming = raw.split(",").map((t) => t.trim()).filter(Boolean);
    if (incoming.length === 0) return;
    const current = (proof.labels || []).map((l) => l.name);
    const next = Array.from(new Set([...current, ...incoming]));
    persistLabelNames(next);
  };

  const toggleLabel = (name) => {
    const current = (proof.labels || []).map((l) => l.name);
    const next = current.includes(name)
      ? current.filter((n) => n !== name)
      : [...current, name];
    persistLabelNames(next);
  };

  // Pool the user can click. Union of project-wide labels + this
  // proof's own labels so a freshly-typed label immediately appears as
  // a checked chip even before the controller refreshes.
  const labelPool = (() => {
    const byName = new Map();
    (projectLabels || []).forEach((l) => byName.set(l.name, l));
    (proof.labels || []).forEach((l) => { if (!byName.has(l.name)) byName.set(l.name, l); });
    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    const response = await destroy(`/api/v1/proofs/${proof.uuid}`, {
      contentType: "application/json",
      responseKind: "json",
    });
    if (response.ok) {
      addToast("success", "Deleted.");
      setDeleted(true);
      setTimeout(() => { window.location.href = `/projects/${projectId}/proofs`; }, 600);
    } else {
      setDeleting(false);
      setConfirmOpen(false);
      addToast("error", "Something went wrong. Please try again.");
    }
  };

  const handleSaveAsImage = async () => {
    if (!cardRef.current || savingImage) return;
    setSavingImage(true);
    setExporting(true);
    await new Promise((r) => setTimeout(r, 50));
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#ffffff",
        style: { border: "none" },
        filter: (node) => {
          const tag = node?.tagName;
          return tag !== "VIDEO" && tag !== "AUDIO";
        },
      });
      const filename = `shosay-${(proof.name || "testimonial").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${proof.id}.png`;
      const link = document.createElement("a");
      link.download = filename;
      link.href = dataUrl;
      link.click();
      addToast("success", "Image saved!");
    } catch (err) {
      console.error("save-as-image failed", err);
      addToast("error", "Could not save the image. Media may be blocking cross-origin export.");
    } finally {
      setExporting(false);
      setSavingImage(false);
    }
  };

  if (deleted) {
    return <div className="p-8 text-center text-slate-600 zz-hand text-2xl">redirecting…</div>;
  }

  return (
    <div className="pb-16">
      <Toasts toasts={toasts} setToasts={setToasts} />

      <Modal modalOpen={confirmOpen} setModalOpen={setConfirmOpen} maxWidth="max-w-lg">
        <div className="p-1">
          <p className="zz-hand text-2xl text-ssred leading-none">no takebacks!</p>
          <h2 className="zz-display text-2xl font-black mt-1">Delete this {proof.kind}?</h2>
          <p className="text-sm text-slate-600 mt-3">
            From <span className="font-bold text-slate-900">{proof.name || "Anonymous"}</span>
            {proof.headline && <>, {"\u201C"}{proof.headline}{"\u201D"}</>}.
          </p>
          <p className="text-sm text-slate-600 mt-2">
            This cannot be undone. It disappears from every wall and widget it
            appears on{widgets.length > 0 && <> ({widgets.length})</>}.
          </p>
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={() => setConfirmOpen(false)} className="zz-btn sm">
              keep it
            </button>
            <button type="button" onClick={handleDelete} disabled={deleting}
                    className={`zz-btn sm red ${deleting ? "opacity-50 cursor-not-allowed" : ""}`}>
              {deleting ? "deleting…" : "delete for good"}
            </button>
          </div>
        </div>
      </Modal>

      <div className="flex flex-wrap gap-4 justify-center items-center mb-6">
        {prevUrl
          ? <a href={prevUrl} data-turbo="false" className="zz-btn sm orange">← previous</a>
          : <span className="zz-btn sm orange opacity-40 cursor-not-allowed" aria-disabled="true">← previous</span>}
        <a href={`/projects/${proof.project_id}/proofs/${proof.id}/edit`} className="zz-btn sm blue">edit</a>
        {proof.kind === "text" && (
          <button type="button" onClick={handleSaveAsImage} disabled={savingImage} className={`zz-btn sm yellow ${savingImage ? "opacity-50 cursor-not-allowed" : ""}`}>
            {savingImage ? "saving…" : "save as image"}
          </button>
        )}
        <button type="button" onClick={handleApproveChange} className="zz-btn sm green">
          {proof.approved ? "unapprove" : "approve"}
        </button>
        <button type="button" onClick={() => setConfirmOpen(true)} className="zz-btn sm red">
          delete
        </button>
        {nextUrl
          ? <a href={nextUrl} data-turbo="false" className="zz-btn sm orange">next →</a>
          : <span className="zz-btn sm orange opacity-40 cursor-not-allowed" aria-disabled="true">next →</span>}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className={`zz-chip ${proof.approved ? "green" : "yellow"}`}>
          {proof.approved ? "approved" : "waiting"}
        </span>
        <span className="inline-flex items-center gap-1.5 text-sm text-slate-600 font-bold">
          {kindIcon(proof)}
          <span className="capitalize">{proof.kind}</span>
        </span>
        <span className="text-slate-300">·</span>
        <time className="text-sm text-slate-600 font-bold" dateTime={proof.given_at_utc}>{proof.given_at_utc}</time>
        <span className="text-slate-300">·</span>
        <span className="text-sm text-slate-600 font-bold capitalize">{proof.source_platform}</span>
      </div>

      <div className="relative mb-8">
        <div ref={cardRef} className="zz-panel p-6 sm:p-10">
          {/* Sits at top:-12px, outside the card's own box. html-to-image
              captures the card bounds, so it lands in the export as a
              clipped sliver rather than tape, so leave it out entirely. */}
          {!exporting && (
            <div className="zz-tape" style={{ background: "rgba(255,212,58,0.85)" }}></div>
          )}

          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6">
            <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-xl border-[3px] border-black shrink-0 overflow-hidden flex items-center justify-center shadow-[4px_4px_0_#111]" style={{ background: "#8B5CF6" }}>
              {proof.avatar ? (
                <img src={proof.avatar} alt={proof.name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-white font-black text-3xl">{(proof.name || "?")[0].toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="zz-hand text-2xl text-ssblue leading-none -rotate-1">from —</p>
              <div className="zz-display text-3xl sm:text-4xl font-black mt-1 leading-tight">{proof.name}</div>
              {proof.source_tagline && (
                <div className="mt-1 text-lg font-bold text-slate-700">{proof.source_tagline}</div>
              )}
              {(proof.contact_title || proof.contact_company_name) && (
                <p className="mt-1 text-sm text-slate-600">
                  {proof.contact_title}
                  {proof.contact_title && proof.contact_company_name && " · "}
                  {proof.contact_company_name && <span className="font-bold" style={{ color: "#3E8DFF" }}>{proof.contact_company_name}</span>}
                </p>
              )}
            </div>
          </div>

          {proof.headline && (
            <h1 className="zz-display text-2xl sm:text-3xl font-black leading-tight mb-4">
              <span className="zz-highlight yellow">{proof.headline}</span>
            </h1>
          )}

          {proof.kind === "video" && !exporting && (
            <video controls className="w-full rounded-xl border-[3px] border-black shadow-[4px_4px_0_#111]" src={proof.video_url} />
          )}
          {proof.kind === "video" && exporting && (
            <div
              className="aspect-video w-full rounded-xl border-[3px] border-black shadow-[4px_4px_0_#111] flex flex-col items-center justify-center text-white"
              style={{ background: "linear-gradient(135deg, #2a2a2a, #111)" }}
            >
              <svg width="96" height="96" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.85 }}>
                <path d="M8 5v14l11-7z" />
              </svg>
              <div className="mt-2 text-xs font-semibold uppercase tracking-widest" style={{ opacity: 0.7 }}>Video testimonial</div>
            </div>
          )}
          {proof.kind === "audio" && !exporting && (
            <video controls className="w-full h-14 rounded-xl border-[3px] border-black shadow-[4px_4px_0_#111]" src={proof.audio_url} />
          )}
          {proof.kind === "audio" && exporting && (
            <div className="w-full h-14 rounded-xl border-[3px] border-black shadow-[4px_4px_0_#111] flex items-center gap-3 px-4 text-white" style={{ background: "linear-gradient(90deg, #2a2a2a, #111)" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ opacity: 0.85 }}>
                <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM5 10v2a7 7 0 006 6.92V21H9a1 1 0 100 2h6a1 1 0 100-2h-2v-2.08A7 7 0 0019 12v-2h-2v2a5 5 0 01-10 0v-2H5z" />
              </svg>
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ opacity: 0.7 }}>Audio testimonial</div>
            </div>
          )}
          {proof.kind === "text" && (
            <div className="prose-sm text-base text-slate-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: proof.body }} />
          )}
        </div>
      </div>

      <div className="zz-panel p-4 sm:p-5 mb-6 max-w-lg mx-auto">
        <div className="flex flex-col gap-3">
          <div className="zz-hand text-lg text-sspink leading-none -rotate-1">labels —</div>
          <p className="text-xs text-slate-600">
            Click an existing label to toggle it, or type a new one (comma-separated for multiple).
            {labelsUrl && (<> Manage all your labels <a href={labelsUrl} className="zz-link">here</a>.</>)}
          </p>

          {labelPool.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {labelPool.map((label) => {
                const on = (proof.labels || []).some((l) => l.name === label.name);
                return (
                  <button
                    key={label.name}
                    type="button"
                    onClick={() => toggleLabel(label.name)}
                    title={on ? "Applied — click to remove" : "Click to apply"}
                    className={
                      "px-2.5 py-1 text-xs font-bold rounded-full border-[2px] transition-colors inline-flex items-center gap-1 " +
                      (on
                        ? "border-black bg-sspink text-white"
                        : "border-slate-300 bg-white text-slate-400 hover:text-slate-700 hover:border-slate-500")
                    }
                  >
                    {on && <span aria-hidden="true" className="leading-none">✓</span>}
                    <span>{label.name}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic">No labels in this project yet — add the first one below.</p>
          )}

          <input
            type="text"
            defaultValue=""
            key={(proof.labels || []).map((l) => l.name).join("|")}
            onBlur={(e) => { handleLabelsAdd(e.target.value); e.target.value = ""; }}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
            placeholder="add new — talent, customer, big-deal"
            className="px-3 py-2 text-sm border-[2px] border-black rounded bg-white focus:outline-none focus:ring-2 focus:ring-sspink"
            aria-label="Add new labels"
          />
        </div>
      </div>

      <div className="mt-12">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="zz-display text-2xl sm:text-3xl font-black">
            appears on <span className="zz-highlight pink">widgets</span>.
          </h2>
          <span className="zz-hand text-lg text-slate-500">{widgets.length}</span>
        </div>

        {widgets.length === 0 ? (
          <div className="zz-panel p-6 text-center max-w-lg mx-auto">
            <p className="zz-hand text-xl text-slate-500">nothing yet —</p>
            <p className="text-sm text-slate-600 mt-1">
              This {proof.kind} proof isn't on any widgets. Add it to one from the widget editor.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            {widgets.map((w) => (
                <div key={w.id} className="zz-panel">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="zz-display text-lg font-black truncate">{w.name || `Widget ${w.id}`}</div>
                      <div className="text-xs text-slate-500 font-bold mt-0.5">
                        {w.kind}
                        {w.automatic && <> · <span className="capitalize">{w.automatic}</span></>}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-3">
                    <a href={w.public_url} target="_blank" rel="noopener noreferrer" className="zz-link muted text-xs">view public ↗</a>
                    <a href={w.edit_url} className="zz-btn sm blue">edit</a>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProofManage;
